/**
 * SessionService - Intelligent Session Management for Hybrid Applications
 * 
 * This service addresses the common issue where session_start events are triggered
 * on every page refresh/reload, which inflates analytics metrics and doesn't reflect
 * actual user sessions.
 * 
 * Key Features:
 * - Session Persistence: Uses localStorage to maintain session state across page reloads
 * - Intelligent Session Detection: Only starts new session if no valid existing session
 * - Activity Tracking: Monitors user activity to keep sessions alive
 * - Automatic Timeout: Sessions expire after 30 minutes of inactivity
 * - Proper Session End: Tracks session end on page unload and timeout
 * - Platform Detection: Differentiates between hybrid app, mobile web, and desktop web
 * - Hybrid App Optimized: Designed for apps that load once but may refresh
 * - Auth Page Exclusion: No sessions started on login/register/forgot-password pages
 * 
 * Session Lifecycle:
 * 1. Page Load: Check if current page is auth page - if yes, skip session initialization
 * 2. If not auth page: Check for existing valid session in localStorage
 * 3. If valid session exists: Resume it (no new session_start event)
 * 4. If no valid session and not auth page: Start new session (track session_start)
 * 5. During session: Track user activity to prevent timeout
 * 6. On timeout/inactivity: End session and start new one (only on non-auth pages)
 * 7. On page unload: Track session_end with duration
 * 8. On successful login: Manually start new session
 * 
 * This is especially important for hybrid apps where:
 * - The WebView loads the page once when app starts
 * - Users might refresh the page (F5) for various reasons
 * - Each refresh shouldn't be treated as a new app session
 * - True sessions should reflect actual user engagement periods
 * - Authentication pages should not generate session events
 */

import { trackSession } from '../third-party/MixPanel';

interface SessionData {
    sessionId: string;
    startTime: number;
    lastActivity: number;
}

class SessionService {
    private static readonly SESSION_KEY = 'app_session';
    private static readonly SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    private static instance: SessionService;
    private sessionData: SessionData | null = null;
    private activityTimer: NodeJS.Timeout | null = null;
    private isEnding: boolean = false; // Prevent multiple session end calls
    private eventListenersAttached: boolean = false; // Prevent duplicate event listeners
    private ignoreUnloadEvents: boolean = false; // Flag to temporarily ignore unload events

    private constructor() {
        this.initializeSession();
        this.setupActivityTracking();
        this.setupBeforeUnloadHandler();
    }

    public static getInstance(): SessionService {
        if (!SessionService.instance) {
            SessionService.instance = new SessionService();
        }
        return SessionService.instance;
    }

    private generateSessionId(): string {
        return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Check if current page is an authentication page where sessions should not be started
     */
    private isAuthPage(): boolean {
        const pathname = window.location.pathname.toLowerCase();
        const authPaths = ['/login', '/register', '/forgot-password', '/reset-password'];

        return authPaths.some(path => pathname.includes(path)) ||
            pathname === '/' && window.location.hash.toLowerCase().includes('#/login') ||
            pathname === '/' && window.location.hash.toLowerCase().includes('#/register') ||
            pathname === '/' && window.location.hash.toLowerCase().includes('#/forgot-password');
    }

    private initializeSession(): void {
        // Don't start sessions on authentication pages
        if (this.isAuthPage()) {
            console.log('Skipping session initialization on auth page');
            return;
        }

        const stored = localStorage.getItem(SessionService.SESSION_KEY);
        const now = Date.now();

        if (stored) {
            try {
                const parsedSession: SessionData = JSON.parse(stored);

                // Check if session is still valid (not expired)
                if (now - parsedSession.lastActivity < SessionService.SESSION_TIMEOUT) {
                    // Valid existing session - just update activity
                    this.sessionData = parsedSession;
                    this.updateActivity();
                    console.log('Resumed existing session:', parsedSession.sessionId);
                    return;
                } else {
                    // Session expired - end it and start new one
                    console.log('Previous session expired, starting new one');
                    this.endSession(false); // Don't track end for expired session
                }
            } catch (e) {
                console.warn('Invalid session data in localStorage, starting fresh');
            }
        }

        // Start new session only if not on auth page
        this.startNewSession();
    }

    private startNewSession(): void {


        this.sessionData = {
            sessionId: this.generateSessionId(),
            startTime: Date.now(),
            lastActivity: Date.now()
        };

        localStorage.setItem(SessionService.SESSION_KEY, JSON.stringify(this.sessionData));

        // Track session start
        trackSession('start', {
            sessionId: this.sessionData.sessionId,
            platform: this.getPlatform()
        });

        console.log('Started new session:', this.sessionData.sessionId);
    }

    private updateActivity(): void {
        if (this.sessionData && !this.isEnding) {
            this.sessionData.lastActivity = Date.now();
            localStorage.setItem(SessionService.SESSION_KEY, JSON.stringify(this.sessionData));
        }
    }

    private setupActivityTracking(): void {
        // Prevent duplicate event listeners
        if (this.eventListenersAttached) {
            return;
        }

        // Track user activity to keep session alive
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

        const throttledUpdateActivity = this.throttle(() => {
            // Only update activity if we have an active session and not on auth page
            if (!this.isAuthPage()) {
                this.updateActivity();
            }
        }, 30000); // Update activity at most once every 30 seconds

        events.forEach(event => {
            document.addEventListener(event, throttledUpdateActivity as EventListener, { passive: true });
        });

        // Set up automatic session timeout check
        this.activityTimer = setInterval(() => {
            this.checkSessionTimeout();
        }, 60000); // Check every minute

        this.eventListenersAttached = true;
    }

    private checkSessionTimeout(): void {
        // Don't check session timeout on auth pages
        if (this.isAuthPage()) {
            return;
        }

        if (this.sessionData && !this.isEnding) {
            const now = Date.now();
            if (now - this.sessionData.lastActivity >= SessionService.SESSION_TIMEOUT) {
                console.log('Session timed out due to inactivity');
                this.endSession(true);
                // Only start new session if not on auth page
                if (!this.isAuthPage()) {
                    this.startNewSession();
                }
            }
        }
    }

    private setupBeforeUnloadHandler(): void {
        // Only add if not already added
        if (!this.eventListenersAttached) {
            // Handle page unload/close - be conservative, only track on actual page close
            window.addEventListener('beforeunload', () => {
                // Only end session if we're not in the middle of a manual session operation
                if (!this.isEnding && this.sessionData && !this.ignoreUnloadEvents) {
                    this.endSession(true);
                }
            });

            // Handle visibility change (page hidden/shown) - more reliable for detecting actual page close
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible' && !this.isEnding) {
                    // Page became visible - check if session is still valid
                    this.initializeSession();
                }
            });

            // Also handle the 'pagehide' event which is more reliable for mobile
            window.addEventListener('pagehide', () => {
                if (!this.isEnding && this.sessionData && !this.ignoreUnloadEvents) {
                    this.endSession(true);
                }
            });
        }
    }

    private endSession(trackEvent: boolean = true): void {
        // Prevent multiple calls to endSession
        if (this.isEnding || !this.sessionData) {
            return;
        }

        this.isEnding = true;

        if (trackEvent) {
            const duration = Date.now() - this.sessionData.startTime;

            trackSession('end', {
                sessionId: this.sessionData.sessionId,
                duration: Math.round(duration / 1000), // Duration in seconds
                platform: this.getPlatform()
            });

            console.log('Ended session:', this.sessionData.sessionId, 'Duration:', Math.round(duration / 1000), 'seconds');
        }

        // Clear session data
        this.sessionData = null;
        localStorage.removeItem(SessionService.SESSION_KEY);

        if (this.activityTimer) {
            clearInterval(this.activityTimer);
            this.activityTimer = null;
        }

        // Reset the ending flag after a short delay to allow for new session creation
        setTimeout(() => {
            this.isEnding = false;
        }, 100);
    }

    private getPlatform(): string {
        // Detect platform context
        if (window.location.href.includes('localhost') || window.location.href.includes('192.168')) {
            return 'development';
        }

        // Check if running in a WebView (hybrid app)
        const userAgent = navigator.userAgent;
        if (userAgent.includes('wv') || userAgent.includes('Version/') && userAgent.includes('Mobile')) {
            return 'hybrid_mobile';
        }

        // Check if mobile browser
        if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
            return 'mobile_web';
        }

        return 'desktop_web';
    }

    private throttle(func: Function, limit: number): Function {
        let inThrottle: boolean;
        return function (this: any, ...args: any[]) {
            if (!inThrottle) {
                func.apply(this, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }

    // Public methods
    public getCurrentSessionId(): string | null {
        return this.sessionData?.sessionId || null;
    }

    public getSessionDuration(): number {
        if (this.sessionData) {
            return Date.now() - this.sessionData.startTime;
        }
        return 0;
    }

    public forceEndSession(): void {
        this.endSession(true);
    }

    public forceStartNewSession(): void {
        this.endSession(true);
        // Wait a bit before starting new session to ensure cleanup
        setTimeout(() => {
            this.startNewSession();
        }, 150);
    }

    /**
     * Manually start a session - typically called after successful login
     */
    public startSessionAfterLogin(): void {
        // End any existing session first
        if (this.sessionData) {
            this.endSession(false); // Don't track end event for login transition
        }

        // Start new session
        this.startNewSession();
    }

    /**
     * Check if we should start a session on current page
     */
    public shouldStartSession(): boolean {
        return !this.isAuthPage();
    }

    /**
     * Temporarily ignore unload events (useful during navigation)
     * @param duration Duration in milliseconds to ignore events
     */
    public temporarilyIgnoreUnloadEvents(duration: number = 2000): void {
        this.ignoreUnloadEvents = true;
        setTimeout(() => {
            this.ignoreUnloadEvents = false;
        }, duration);
    }

    /**
     * Enable or disable unload event tracking
     * @param enabled Whether to track unload events
     */
    public setUnloadEventTracking(enabled: boolean): void {
        this.ignoreUnloadEvents = !enabled;
    }
}

// Export singleton instance
export const sessionService = SessionService.getInstance();
export default sessionService; 